/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BasicInput from '../../../components/BasicInput';
import BasicOption from '../../../components/BasicOption';
import ButtonTextIcon, { BtnType } from '../../../components/BtnIcon';
import { ModalActionType, ModalProps } from '../../../components/Modal';
import api from '../../../config/axios';
import DefaultLayout from '../../../Layout';

type ProcedureProps = {
    id: number,
    categoryProceduresId: number,
    accreditedId: number,
    description: string,
    value: string,
    observation: null | string,
}

type AccrecreditedProps = {
    id: number;
    name: string
}

type CategoryProps = {
    id: number;
    title: string;
}

export default function EditProcedure() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showToastSuccess, setShowToastSuccess] = useState(false);
  const [showToastError, setShowToastError] = useState(false);
  const [accrediteds, setAccrediteds] = useState<AccrecreditedProps[]>([]);
  const [categories, setCategories] = useState<CategoryProps[]>([]);
  const [selectedAccredited, setSelectedAccredited] = useState<AccrecreditedProps>({
    id: 10000000,
    name: 'SELECIONE',
  });
  const [selectedCategory, setSelectedCategory] = useState<CategoryProps>({
    id: 10000000,
    title: 'SELECIONE',
  });
  const [modal, setModal] = useState<ModalProps | null>(null);
  const [token] = useState({ headers: { Authorization: `bearer ${localStorage.getItem('adminToken')}` } });
  const [procedures, setProcedures] = useState<ProcedureProps>({
    id: 100000000,
    categoryProceduresId: 100000,
    accreditedId: 1000000000,
    description: '',
    value: '',
    observation: '',
  });

  function enableButton() {
    return procedures.description !== '';
  }

  async function fetchPreload(): Promise<{
    accrediteds: AccrecreditedProps[],
    categories: CategoryProps[]
  }> {
    try {
      setLoading(true);
      const response = await api.get('/admin/medical-preload', token);
      setAccrediteds(response.data.accrediteds);
      setCategories(response.data.categories);
      return {
        accrediteds: response.data.accrediteds,
        categories: response.data.categories,
      };
    } catch (err: any) {
      console.log(err);
      setLoading(false);
      return {
        accrediteds: [],
        categories: [],
      };
    }
  }

  async function fetchProcedures() {
    try {
      setLoading(true);
      const preload = await fetchPreload();
      if (preload.accrediteds.length === 0) {
        setModal({ title: 'Ocorreu um erro', children: 'Não foi possível carregar lista de credenciados.', actions: [{ label: 'Ok, entendi', type: ModalActionType.PRIMARY, onClick: () => navigate(-1) }] });
        return;
      }
      if (preload.categories.length === 0) {
        setModal({ title: 'Ocorreu um erro', children: 'Não foi possível carregar lista de categorias.', actions: [{ label: 'Ok, entendi', type: ModalActionType.PRIMARY, onClick: () => navigate(-1) }] });
        return;
      }
      const response = await api.get<ProcedureProps>(`admin/medical-procedures/${id}`, token);
      setProcedures(response.data);
      setSelectedAccredited(preload.accrediteds.filter((item) => item.id === response.data.accreditedId)[0]);
      setSelectedCategory(preload.categories.filter((item) => item.id === response.data.categoryProceduresId)[0]);
      setLoading(false);
    } catch (err: any) {
      console.log(err.response);
      setModal({ title: 'Ocorreu um erro', children: 'Não foi possível buscar exame.', actions: [{ label: 'Ok, entendi', type: ModalActionType.PRIMARY, onClick: () => navigate(-1) }] });
    }
  }

  async function saveProcedure() {
    try {
      setLoading(true);
      await api.put(`admin/medical-procedures/${id}`, {
        ...procedures,
        accreditedId: selectedAccredited.id,
        categoryProceduresId: selectedCategory.id,
        observation: procedures.observation === '' ? null : procedures.observation,
      }, token);
      setLoading(false);
      setShowToastSuccess(true);
      setTimeout(() => {
        setShowToastSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.log(err.response);
      setModal({ title: 'Ocorreu um erro', children: 'Não foi possível editar procedimento.', actions: [{ label: 'Ok, entendi', type: ModalActionType.PRIMARY, onClick: () => setModal(null) }] });
    }
  }

  async function deleteProcedure() {
    try {
      setLoading(true);
      await api.delete(`admin/medical-procedures/${id}`, token);
      setLoading(false);
      navigate(-1);
    } catch (err: any) {
      console.log(err.response);
      setModal({ title: 'Ocorreu um erro', children: 'Não foi possível editar procedimento.', actions: [{ label: 'Ok, entendi', type: ModalActionType.PRIMARY, onClick: () => setModal(null) }] });
    }
  }

  useEffect(() => {
    fetchProcedures();
  }, []);

  return (
    <DefaultLayout
      pageTitle="Visualizar procedimento"
      loading={loading}
      showToastSuccess={showToastSuccess}
      showToastError={showToastError}
      showRightSlider={false}
      rightSliderContent={undefined}
      modal={modal}
      setCloseRightSlider={() => null}
    >
      <div className="row seller">
        <div className="col-12 p-0 card-bg p-4">
          <h1>Visualizar Procedimento</h1>
          <div className="row">
            <div className="col-4">
              <BasicInput
                id="title-input"
                label="Descrição"
                readOnly={false}
                helper="Biópsia"
                error={procedures.description.length < 1 ? 'Campo obrigatório' : ''}
                value={procedures.description}
                type="text"
                onChange={(data) => setProcedures({ ...procedures, description: data })}
              />
            </div>
            <div className="col-4">
              <BasicInput
                id="value-input"
                label="Valor"
                readOnly={false}
                helper="Ex: 100,00"
                error=""
                value={procedures.value}
                type="text"
                onChange={(data) => setProcedures({ ...procedures, value: data })}
              />
            </div>
            <div className="col-4">
              <BasicOption
                id="category-input"
                label="Categoria"
                readOnly={false}
                helper="Ex: Biópsia"
                error=""
                value={selectedCategory.title}
                onChange={(data) => setSelectedCategory(categories.filter((item) => item.title === data)[0])}
              >
                {categories.map((item) => item.title)}
              </BasicOption>
            </div>
            <div className="col-4">
              <BasicOption
                id="accredited-input"
                label="Credenciado"
                readOnly={false}
                helper="Ex: HSM"
                error=""
                value={selectedAccredited.name}
                onChange={(data) => setSelectedAccredited(accrediteds.filter((item) => item.name === data)[0])}
              >
                {accrediteds.map((item) => item.name)}
              </BasicOption>
            </div>
            <div className="col-8">
              <BasicInput
                id="observation-input"
                label="Observação"
                readOnly={false}
                helper=""
                error=""
                value={procedures.observation || ''}
                type="text"
                onChange={(data) => setProcedures({ ...procedures, observation: data })}
              />
            </div>
            <div className="col-12 footer">
              <ButtonTextIcon
                label="EXCLUIR"
                type={BtnType.DANGER}
                icon="delete"
                active
                onClick={() => deleteProcedure()}
                ref={null}
              />
              <ButtonTextIcon
                label="Salvar"
                type={BtnType.PRIMARY}
                icon="backup"
                active={enableButton()}
                onClick={() => saveProcedure()}
                ref={null}
              />
            </div>
          </div>
        </div>
      </div>

    </DefaultLayout>
  );
}
